import React, { useEffect, useMemo, useState } from 'react';

import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { TreeView } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StyledTreeItem } from 'src/components/TreeItem';
import { IndicatorWithAlerts } from 'src/types/indicator-alert-configuration';

import { AlertNode } from './components/AlertNode';
import { EmptyAlertsState } from './components/EmptyAlertsState';

type AlertTreeViewProps = {
  indicatorsWithAlerts: IndicatorWithAlerts[];
  onAddAlert: (indicatorKey: string) => void;
  onUpdateAlert: (
    indicatorKey: string,
    alertId: string,
    field: string,
    value: any,
  ) => void;
  onRemoveAlert: (
    indicatorKey: string,
    alertId: string,
    id: number | null,
  ) => void;
};

export function AlertTreeView({
  indicatorsWithAlerts = [],
  onAddAlert,
  onUpdateAlert,
  onRemoveAlert,
}: AlertTreeViewProps) {
  const theme = useTheme();

  const calculateAutoExpandedNodes = useMemo(() => {
    const expanded = ['root'];

    indicatorsWithAlerts.forEach((indicator) => {
      const hasRelevantAlerts = indicator.alerts?.some((alert) => {
        const isValidValue = (value: number | null | undefined): boolean => {
          return (
            value !== null &&
            value !== undefined &&
            !isNaN(value) &&
            value !== 0
          );
        };

        return (
          alert.id !== null ||
          isValidValue(alert.startValue) ||
          isValidValue(alert.endValue) ||
          alert.isActive === true
        );
      });

      if (hasRelevantAlerts) {
        expanded.push(indicator.key);
      }
    });

    return expanded;
  }, [indicatorsWithAlerts]);

  const [expanded, setExpanded] = useState<string[]>(
    calculateAutoExpandedNodes,
  );
  const [manuallyCollapsed, setManuallyCollapsed] = useState<string[]>([]);
  const [manuallyExpanded, setManuallyExpanded] = useState<string[]>([]);

  useEffect(() => {
    setExpanded((prev) => {
      const newAutoExpanded = calculateAutoExpandedNodes;

      const shouldExpand = [
        ...newAutoExpanded.filter(
          (nodeId) => !manuallyCollapsed.includes(nodeId),
        ),
        ...manuallyExpanded.filter(
          (nodeId) => !manuallyCollapsed.includes(nodeId),
        ),
      ];

      const combined = [...new Set(shouldExpand)];

      return combined;
    });
  }, [calculateAutoExpandedNodes, manuallyCollapsed, manuallyExpanded]);

  function handleToggle(event: React.SyntheticEvent, nodeIds: string[]) {
    const previousExpanded = expanded;

    const newlyCollapsed = previousExpanded.filter(
      (nodeId) => !nodeIds.includes(nodeId) && nodeId !== 'root',
    );

    const newlyExpanded = nodeIds.filter(
      (nodeId) => !previousExpanded.includes(nodeId) && nodeId !== 'root',
    );

    if (newlyCollapsed.length > 0) {
      setManuallyCollapsed((prev) => [
        ...new Set([...prev, ...newlyCollapsed]),
      ]);
      setManuallyExpanded((prev) =>
        prev.filter((nodeId) => !newlyCollapsed.includes(nodeId)),
      );
    }

    if (newlyExpanded.length > 0) {
      setManuallyExpanded((prev) => [...new Set([...prev, ...newlyExpanded])]);
      setManuallyCollapsed((prev) =>
        prev.filter((nodeId) => !newlyExpanded.includes(nodeId)),
      );
    }

    setExpanded(nodeIds);
  }

  useEffect(() => {
    const currentKeys = indicatorsWithAlerts.map((ind) => ind.key);
    const hasStructuralChange = manuallyCollapsed.some(
      (key) => !currentKeys.includes(key),
    );

    if (hasStructuralChange) {
      setManuallyCollapsed([]);
    }
  }, [indicatorsWithAlerts.map((ind) => ind.key).join(',')]);
  useEffect(() => {
    const currentKeys = indicatorsWithAlerts.map((ind) => ind.key);

    const hasStructuralChange =
      manuallyCollapsed.some((key) => !currentKeys.includes(key)) ||
      manuallyExpanded.some((key) => !currentKeys.includes(key));

    if (hasStructuralChange) {
      setManuallyCollapsed([]);
      setManuallyExpanded([]);
    }
  }, [indicatorsWithAlerts.map((ind) => ind.key).join(',')]);

  if (indicatorsWithAlerts.length === 0) {
    return <EmptyAlertsState />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TreeView
        defaultCollapseIcon={<ExpandMore htmlColor="#acacac" />}
        defaultExpandIcon={<ChevronRight htmlColor="#acacac" />}
        expanded={expanded}
        onNodeToggle={handleToggle}
        sx={{
          flexGrow: 1,
          maxWidth: '100%',
          overflowY: 'auto',
        }}>
        <StyledTreeItem
          rootnode
          nodeId="root"
          label={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                border: '2px solid #e0e0e0',
                mb: 1,
              }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#424242',
                  fontWeight: 600,
                }}>
                Indicadores de&nbsp;
                {indicatorsWithAlerts[0]?.valueType === 'percentage'
                  ? 'Porcentagem'
                  : 'Montante'}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}>
                <Box
                  sx={{
                    color: theme.palette.secondary.light,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}>
                  {indicatorsWithAlerts.length} indicadores
                </Box>

                {calculateAutoExpandedNodes.length && (
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}>
                    {calculateAutoExpandedNodes.length - 1} com alertas
                  </Box>
                )}
              </Box>
            </Box>
          }>
          {indicatorsWithAlerts.map((indicator, index) => (
            <AlertNode
              key={`${indicator.key}-${index}`}
              indicatorIndex={index}
              indicator={indicator}
              onUpdateAlert={onUpdateAlert}
              onAddAlert={onAddAlert}
              onRemoveAlert={onRemoveAlert}
            />
          ))}
        </StyledTreeItem>
      </TreeView>
    </Box>
  );
}
